import { erroresPorCampo, traducirError, traducirProblema, useTraduccion, type ProblemaApi } from '@tallerya/i18n';
import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { vacio } from './valores';

// El patrón de formulario (F-8), la parte sin pantalla: los valores, la
// validación, los errores de la API y el estado del guardado.
//
// ## Un solo idioma para los errores: los códigos
//
// La validación de acá y la de la API hablan igual: devuelven **códigos**
// —`validation.required`—, nunca texto. Los dos pasan por el mismo `traducirError`
// y salen en el idioma de la pantalla. Así un campo vacío dice lo mismo lo
// detecte el navegador o el servidor, y la API no necesita saber en qué idioma
// está nadie (la regla de B-21).
//
// La validación de acá es una **cortesía**, no una defensa: ahorra el viaje de
// ida y vuelta por un campo obligatorio vacío. La que vale es la del servidor, y
// por eso el formulario funciona igual si una maestra no declara ninguna.

/**
 * Cuánto espera el error de forma al `click` que sigue a un toque, si no llega.
 * Alcanza para los eventos de compatibilidad del celular y no se nota al salir con Tab.
 */
const ESPERA_CLIC_MS = 500;

/**
 * Cuánto espera, como mucho, a que se suelte un puntero que se ve apretado. Más que
 * cualquier clic normal, para no mover la pantalla bajo un dedo que sigue encima.
 */
const ESPERA_SOLTAR_MS = 3000;

/** Lo que cuenta como soltar el puntero. El menú contextual, porque tras él el `pointerup` puede no llegar. */
const EVENTOS_SOLTAR = ['pointerup', 'pointercancel', 'contextmenu'];

/** El 409 de una edición que se quedó vieja (#77): otra persona guardó antes. */
const CODIGO_CAMBIO_SIMULTANEO = 'conflict.cambio_simultaneo';

/** Los códigos de error por campo que devuelve `validar`. Sin código, el campo está bien. */
export type ErroresValidacion<V> = Partial<Record<keyof V & string, string>>;

export interface OpcionesFormulario<V> {
  /**
   * Los valores con que arranca. **Se lee una sola vez**, al montarse: si cambia
   * después, el formulario no se entera.
   *
   * Por eso una edición —que carga del servidor— no monta el formulario hasta
   * tener la ficha, y le pone `key` con el id y la versión: otra ficha, u otra
   * versión de la misma, empieza de cero.
   *
   *   if (consulta.isPending) return <Cargando />;
   *   return <Editor key={`${ficha.id}-${ficha.version}`} inicial={aValores(ficha)} />;
   *
   * Así lo hacen `FormularioItem` y `FormularioCliente`. La otra salida,
   * `reiniciar(datos)` cuando llegan, deja un dibujado con los valores vacíos.
   */
  inicial: V;
  /**
   * Revisa los valores antes de mandarlos. Devuelve un código por campo con
   * problema —los mismos de `ValidationCodes.cs`—, o un objeto vacío.
   */
  validar?: (valores: V) => ErroresValidacion<V>;
  /**
   * Manda los valores a la API. Devuelve el `ProblemDetails` si falló, o nada si
   * salió bien. Con openapi-fetch queda en una línea:
   *
   *   guardar: async (v) => (await api.POST('/api/locations', { body: v })).error
   *
   * Si lanza —se cortó la red—, el formulario lo trata como «no hubo respuesta».
   */
  guardar: (valores: V) => Promise<ProblemaApi | undefined | void>;
  /**
   * Errores de la API que no vienen por campo pero son de uno: un 409 de «ese
   * código ya existe» se marca bajo el campo del código, no arriba en el aviso.
   *
   *   camposPorCodigo: { 'location.codigo_tomado': 'code' }
   *
   * Un código que es de varios campos a la vez lleva, por campo, el código cuyo
   * mensaje va debajo de él:
   *
   *   'supplier.nombre_y_documento_duplicados':
   *     { name: 'supplier.nombre_duplicado', docIdentifier: 'supplier.documento_tomado' }
   */
  camposPorCodigo?: Record<string, (keyof V & string) | Partial<Record<keyof V & string, string>>>;
  /**
   * Lo que la pantalla suma al aviso de un error de `camposPorCodigo`: una frase
   * después de «Revisa los N campos…» y una salida, como «Ver proveedor
   * existente →». Recibe el `ProblemDetails` entero, con los datos que traiga.
   */
  completarAviso?: (problema: ProblemaApi) => Partial<AvisoFormulario> | undefined;
  /**
   * Vuelve a pedir el registro cuando otra persona guardó antes
   * (`conflict.cambio_simultaneo`, #178). Con ella, el aviso trae el botón
   * «Recargar»; sin ella, el aviso dice lo mismo pero sin el botón. La pantalla
   * decide qué es recargar: en Clientes, volver a leer la ficha, que trae la
   * versión nueva y arranca el formulario de cero con ella.
   */
  recargar?: () => void;
}

/** Qué dice el aviso rojo de arriba. */
export interface AvisoFormulario {
  titulo: string;
  texto: string;
  /** Una salida que la pantalla agrega al aviso, como «Ver proveedor existente →». */
  enlace?: { texto: string; alHacerClic: () => void };
  /** Un botón a la derecha del aviso, como «Recargar» (#178). */
  accion?: { texto: string; alHacerClic: () => void };
}

export interface EstadoFormulario<V> {
  valores: V;
  cambiar: <K extends keyof V>(campo: K, valor: V[K]) => void;
  /**
   * El error de cada campo, ya traducido. Puede traer además claves de la API que
   * no son un campo de `V` sino una fila de uno —`contacts[0].value`—: la
   * pantalla que dibuja esa lista las busca por su nombre.
   */
  errores: Partial<Record<keyof V & string, string>>;
  aviso: AvisoFormulario | null;
  guardando: boolean;
  /** Valida, guarda y devuelve si salió bien. No lanza. */
  enviar: () => Promise<boolean>;
  /** Vuelve a los valores iniciales —o a otros— y borra errores y aviso. */
  reiniciar: (valores?: V) => void;
  /** El `id` del control de un campo. Lo usan los `Campo*` y el foco al primer error. */
  idDe: (campo: keyof V & string) => string;
  /**
   * Para un campo que tiene algo **a medio escribir** fuera de `valores`: el
   * texto de una etiqueta antes de Enter. `pendiente` recibe el valor actual del
   * campo y devuelve el que queda con lo pendiente incluido, o `undefined` si no
   * hay nada. `enviar` lo pide antes de validar, así lo escrito se guarda aunque
   * nadie haya apretado Enter. Devuelve la función que lo da de baja.
   *
   * Existe para no depender del `blur` (#113): agregar en el `blur` cambiaba el
   * alto de la pantalla entre el `mousedown` y el `mouseup` de «Guardar», y un
   * toque que se soltaba fuera del botón no guardaba nada.
   */
  registrarPendiente: (campo: keyof V & string, pendiente: (actual: unknown) => unknown) => () => void;
  /**
   * Marca un campo como obligatorio mientras está en pantalla: `enviar` no guarda
   * si está vacío. Lo llama el `Campo` que recibe `obligatorio`, así el asterisco
   * y la validación son la misma cosa y no se pueden desencontrar. Devuelve la
   * función que lo da de baja: un campo oculto —la duración de un producto— no frena.
   */
  registrarObligatorio: (campo: keyof V & string) => () => void;
  /**
   * Como `registrarObligatorio`, para la forma del dato: el teléfono, el correo, el
   * número de un documento (#155). `regla` recibe el valor y devuelve el código del
   * error —`validation.phone`— o nada. `enviar` no guarda mientras no se cumpla, y
   * un campo vacío no se revisa: eso es de `obligatorio`. Lo llaman `CampoTelefono`,
   * `CampoCorreo` y `CampoDocumento`. Devuelve la función que la da de baja.
   */
  registrarFormato: (campo: keyof V & string, regla: (valor: unknown) => string | undefined) => () => void;
  /**
   * Revisa la forma de un campo al salir de él, y si no se cumple deja su error
   * debajo, sin aviso arriba: que el teléfono mal escrito se note antes de llegar
   * a «Guardar».
   *
   * **Si se sale tocando algo, espera a que se suelte** (#113): el error agrega una
   * línea bajo el campo, y si apareciera entre el `mousedown` y el `mouseup` de
   * «Guardar», el botón se movería bajo el dedo y el toque no guardaría nada.
   */
  revisarFormato: (campo: keyof V & string) => void;
}


export function useFormulario<V extends object>({
  inicial,
  validar,
  guardar,
  camposPorCodigo,
  completarAviso,
  recargar,
}: OpcionesFormulario<V>): EstadoFormulario<V> {
  const { t } = useTraduccion();
  const [valores, setValores] = useState<V>(inicial);
  const [errores, setErrores] = useState<EstadoFormulario<V>['errores']>({});
  const [aviso, setAviso] = useState<AvisoFormulario | null>(null);
  // Si el aviso es el de «Revisa los N campos», se apaga solo cuando ya no queda
  // ninguno en rojo. Los demás avisos (sin red, un 409) se quedan hasta el próximo intento.
  const [avisoPorCampos, setAvisoPorCampos] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const prefijo = useId();
  // El freno contra el doble clic va en un ref y no en el estado: dos clics en el
  // mismo instante leerían los dos `guardando = false` antes del siguiente dibujado.
  const enCurso = useRef(false);
  // Lo que cada campo tiene a medio escribir, por campo. Un ref: se lee al
  // enviar, no al dibujar.
  const pendientes = useRef(new Map<string, (actual: unknown) => unknown>());

  // Estable, para que el efecto del campo que la usa no se vuelva a correr en
  // cada dibujado.
  const registrarPendiente = useCallback((campo: string, pendiente: (actual: unknown) => unknown) => {
    pendientes.current.set(campo, pendiente);
    return () => {
      if (pendientes.current.get(campo) === pendiente) pendientes.current.delete(campo);
    };
  }, []);

  // Cuántos controles marcan cada campo como obligatorio: una cuenta y no un
  // conjunto, para que el desmontaje de StrictMode —o de un control repetido— no
  // borre la marca del que sigue en pantalla.
  const obligatorios = useRef(new Map<string, number>());

  const registrarObligatorio = useCallback((campo: string) => {
    obligatorios.current.set(campo, (obligatorios.current.get(campo) ?? 0) + 1);
    return () => {
      const quedan = (obligatorios.current.get(campo) ?? 1) - 1;
      if (quedan > 0) obligatorios.current.set(campo, quedan);
      else obligatorios.current.delete(campo);
    };
  }, []);

  // La regla de forma de cada campo. Uno solo por campo: el último que se registra.
  const formatos = useRef(new Map<string, (valor: unknown) => string | undefined>());

  const registrarFormato = useCallback((campo: string, regla: (valor: unknown) => string | undefined) => {
    formatos.current.set(campo, regla);
    return () => {
      if (formatos.current.get(campo) === regla) formatos.current.delete(campo);
    };
  }, []);

  // Los valores de ahora, para revisar un campo desde un evento que llega después
  // del dibujado: el `blur`, o el `pointerup` que lo hizo esperar.
  const vigentes = useRef(valores);
  useEffect(() => {
    vigentes.current = valores;
  }, [valores]);

  // Si hay un dedo o un botón del mouse apretado en la página. Un ref: se lee en
  // el `blur`, no al dibujar.
  //
  // Se suelta también con el menú del clic derecho y cuando la ventana pierde el
  // foco: en los dos casos el `pointerup` puede no llegar nunca, y la marca
  // quedaría puesta atrasando cada error al salir con Tab.
  const apretado = useRef(false);
  useEffect(() => {
    const bajar = () => {
      apretado.current = true;
    };
    const soltar = () => {
      apretado.current = false;
    };
    document.addEventListener('pointerdown', bajar, true);
    for (const evento of EVENTOS_SOLTAR) document.addEventListener(evento, soltar, true);
    window.addEventListener('blur', soltar);
    return () => {
      document.removeEventListener('pointerdown', bajar, true);
      for (const evento of EVENTOS_SOLTAR) document.removeEventListener(evento, soltar, true);
      window.removeEventListener('blur', soltar);
    };
  }, []);

  const revisarFormato = useCallback(
    (campo: string) => {
      const revisar = () => {
        const regla = formatos.current.get(campo);
        const valor = (vigentes.current as Record<string, unknown>)[campo];
        const codigo = regla && !vacio(valor) ? regla(valor) : undefined;
        if (codigo) setErrores((actuales) => ({ ...actuales, [campo]: traducirError(codigo, t) }));
      };

      // Nunca en medio del gesto que sacó el foco. Con el mouse, el `blur` llega
      // con el botón apretado: se espera a soltarlo, y el `click` llega con ese
      // mismo `pointerup`. Con el dedo, el foco se mueve en el `mousedown` de
      // compatibilidad, que llega después del `pointerup`: se espera al `click`
      // que lo sigue, o a la reserva si no hay ninguno —se salió con Tab—. Un turno
      // más en los dos casos, para que el `click` encuentre la pantalla quieta.
      //
      // Con el puntero apretado, la reserva es larga: una corta movería «Guardar»
      // bajo el dedo de quien lo mantiene apretado. Existe para que el error no
      // espere para siempre si el `pointerup` no llega, y por eso suelta la marca.
      const conPuntero = apretado.current;
      const eventos = conPuntero ? EVENTOS_SOLTAR : ['click'];
      const listo = () => {
        for (const evento of eventos) document.removeEventListener(evento, listo, true);
        window.removeEventListener('blur', listo);
        clearTimeout(reserva);
        setTimeout(revisar, 0);
      };
      for (const evento of eventos) document.addEventListener(evento, listo, true);
      if (conPuntero) window.addEventListener('blur', listo);
      const reserva = setTimeout(
        () => {
          if (conPuntero) apretado.current = false;
          listo();
        },
        conPuntero ? ESPERA_SOLTAR_MS : ESPERA_CLIC_MS,
      );
    },
    [t],
  );

  const idDe = (campo: string) => `${prefijo}-${campo}`;
  const titulo = t('ui.formulario.aviso.titulo');

  // Si un error tiene dónde verse: un control con el `id` de su campo, o las filas
  // de una lista (`contacts[0].value` para el error de `contacts`). Se pregunta a
  // la página y no a una lista de campos, porque cada pantalla dibuja los suyos.
  const seVe = (clave: string) =>
    document.getElementById(idDe(clave)) !== null ||
    document.querySelector(`[id^="${CSS.escape(idDe(clave))}["]`) !== null;

  function cambiar<K extends keyof V>(campo: K, valor: V[K]) {
    setValores((actuales) => ({ ...actuales, [campo]: valor }));
    // El error de un campo se borra apenas se toca: seguir en rojo mientras la
    // persona lo está corrigiendo es regañarla dos veces. Un campo que es una
    // lista se lleva también los de sus filas: la API marca `contacts[0].value`,
    // y quien edita la lista está corrigiendo eso.
    const nombre = String(campo);
    const esDelCampo = (clave: string) =>
      clave === nombre || clave.startsWith(`${nombre}[`) || clave.startsWith(`${nombre}.`);
    setErrores((actuales) => {
      if (!Object.keys(actuales).some(esDelCampo)) return actuales;
      return Object.fromEntries(
        Object.entries(actuales).filter(([clave]) => !esDelCampo(clave)),
      ) as EstadoFormulario<V>['errores'];
    });
  }

  /**
   * Pinta los errores por campo, arma el aviso y lleva el foco al primero.
   *
   * `declarados` son los que nombró la propia pantalla —su `validar` o su
   * `camposPorCodigo`—: los dibuja ella, tenga o no un control con ese `id`, como
   * el editor de opciones de un campo de lista. Solo los de un 400 de la API pasan
   * por `seVe`. `extra` es lo que la pantalla suma al aviso (`completarAviso`).
   */
  function mostrarErroresDeCampo(
    porCampo: EstadoFormulario<V>['errores'],
    declarados = false,
    extra?: Partial<AvisoFormulario>,
  ) {
    const visibles = Object.entries(porCampo).filter(([clave]) => declarados || seVe(clave));
    const perdidos = Object.entries(porCampo).filter(([clave]) => !declarados && !seVe(clave));
    if (perdidos.length > 0) console.warn('Errores sin campo en pantalla (#76):', Object.fromEntries(perdidos));
    const campos = visibles.map(([clave]) => clave);
    setErrores(Object.fromEntries(visibles) as EstadoFormulario<V>['errores']);

    if (perdidos.length === 0) {
      const texto = t('ui.formulario.aviso.campos', { n: campos.length });
      setAviso({
        titulo,
        texto: extra?.texto ? `${texto} ${extra.texto}` : texto,
        ...(extra?.enlace && { enlace: extra.enlace }),
      });
      setAvisoPorCampos(true);
    } else {
      // Lo que no se ve no se puede corregir: el aviso se queda hasta el próximo
      // intento. Por eso no dice «revisa los campos en rojo»: los rojos llevan su
      // mensaje abajo y se corrigen solos, y el aviso seguiría señalándolos.
      const mensajes = perdidos.map(([, mensaje]) => String(mensaje ?? '').replace(/\.$/, ''));
      const detalle = [...new Set(mensajes)].join('. ');
      setAviso({ titulo, texto: t('ui.formulario.aviso.fueraDePantalla', { detalle }) });
      setAvisoPorCampos(false);
    }

    // Después del dibujado: el control puede no tener todavía su borde rojo, pero
    // ya existe. El foco en el primero es lo que le dice al lector de pantalla
    // —y a quien tiene el formulario largo— dónde está el problema.
    // El primero de la pantalla, no el primero de la lista: los obligatorios
    // vacíos se suman al final, después de los que dijo `validar`.
    const [primero] = campos
      .map((clave) => document.getElementById(idDe(clave)))
      .filter((control): control is HTMLElement => control !== null)
      .sort((a, b) => (a.compareDocumentPosition(b) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1));
    if (primero) requestAnimationFrame(() => primero.focus());
  }

  async function enviar(): Promise<boolean> {
    if (enCurso.current) return false;

    // Lo que quedó a medio escribir entra antes de validar y de mandar, y queda
    // también en pantalla: es lo que se guardó.
    let actuales = valores;
    for (const [campo, pendiente] of pendientes.current) {
      const con = pendiente((actuales as Record<string, unknown>)[campo]);
      if (con !== undefined) actuales = { ...actuales, [campo]: con };
    }
    if (actuales !== valores) setValores(actuales);

    // Los obligatorios vacíos, sin pisar lo que la pantalla ya dijo de ese campo:
    // su mensaje es más preciso («Falta el nombre del servicio»).
    const locales: Record<string, unknown> = { ...validar?.(actuales) };
    for (const campo of obligatorios.current.keys()) {
      if (!locales[campo] && vacio((actuales as Record<string, unknown>)[campo])) locales[campo] = 'validation.required';
    }
    // Y la forma de los que tienen algo escrito. Esta sí pisa a la pantalla: es la
    // que dijo el campo al salir de él, y cambiarla al guardar desdice al campo (#156).
    for (const [campo, regla] of formatos.current) {
      const valor = (actuales as Record<string, unknown>)[campo];
      const codigo = vacio(valor) ? undefined : regla(valor);
      if (codigo) locales[campo] = codigo;
    }
    const conError = Object.entries(locales).filter(([, codigo]) => codigo);
    if (conError.length > 0) {
      mostrarErroresDeCampo(
        Object.fromEntries(conError.map(([campo, codigo]) => [campo, traducirError(codigo as string, t)])) as EstadoFormulario<V>['errores'],
        true,
      );
      return false;
    }

    enCurso.current = true;
    setGuardando(true);
    // Cada intento empieza limpio: los errores del anterior los vuelve a decir la
    // API si siguen, y si no hubo respuesta no hay con qué sostenerlos.
    setErrores({});
    setAviso(null);
    setAvisoPorCampos(false);
    try {
      const problema = await guardar(actuales);
      if (!problema) return true;

      // 400 con errores por campo: la API ya dijo cuál está mal.
      // Cada campo puede traer varios códigos; debajo del campo se dice el primero.
      const porCampo = Object.entries(erroresPorCampo(problema, t));
      if (porCampo.length > 0) {
        mostrarErroresDeCampo(
          Object.fromEntries(porCampo.map(([campo, mensajes]) => [campo, mensajes[0] ?? ''])) as EstadoFormulario<V>['errores'],
        );
        return false;
      }

      // Un error de un campo que no viene como tal (el 409 de un código repetido),
      // o de varios: cada uno con su mensaje.
      const codigo = problema.code;
      const campos = codigo ? camposPorCodigo?.[codigo] : undefined;
      if (campos && codigo) {
        const porCampo: Partial<Record<string, string>> = typeof campos === 'string' ? { [campos]: codigo } : campos;
        mostrarErroresDeCampo(
          Object.fromEntries(
            Object.entries(porCampo).map(([campo, suyo]) => [campo, traducirError(suyo ?? codigo, t)]),
          ) as EstadoFormulario<V>['errores'],
          true,
          completarAviso?.(problema),
        );
        return false;
      }

      // Cualquier otro: se dice en el aviso, traducido, y si la orden rechazó
      // varias cosas, todas juntas (#130). Sin código, no hubo una respuesta que
      // entender —un 502 del proxy, por ejemplo—.
      // El cambio simultáneo no se arregla corrigiendo nada: hace falta la versión
      // nueva. Si la pantalla sabe traerla, el aviso ofrece hacerlo.
      setAviso({
        titulo,
        texto: problema.code ? traducirProblema(problema, t) : t('ui.formulario.aviso.sinRespuesta'),
        ...(recargar &&
          codigo === CODIGO_CAMBIO_SIMULTANEO && {
            accion: { texto: t('ui.formulario.recargar'), alHacerClic: recargar },
          }),
      });
      return false;
    } catch {
      // `fetch` lanza cuando no hubo respuesta: sin red, o el servidor caído.
      setAviso({ titulo, texto: t('ui.formulario.aviso.sinRespuesta') });
      return false;
    } finally {
      enCurso.current = false;
      setGuardando(false);
    }
  }

  function reiniciar(nuevos?: V) {
    setValores(nuevos ?? inicial);
    setErrores({});
    setAviso(null);
    setAvisoPorCampos(false);
  }

  const avisoVigente = avisoPorCampos && Object.keys(errores).length === 0 ? null : aviso;

  return {
    valores,
    cambiar,
    errores,
    aviso: avisoVigente,
    guardando,
    enviar,
    reiniciar,
    idDe,
    registrarPendiente,
    registrarObligatorio,
    registrarFormato,
    revisarFormato,
  };
}
